import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { ClipboardList, RotateCcw, Save, Loader2, Eye, Pencil, Plus } from 'lucide-react';
import { SectionEditor } from './FormTemplateEditor';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PROFILE_LABELS = {
  infantil: 'Infantil',
  adolescente: 'Adolescente',
  adulto: 'Adulto',
  tercera_edad: 'Tercera Edad',
};

const PROFILE_COLORS = {
  infantil: 'bg-pink-100 text-pink-700',
  adolescente: 'bg-blue-100 text-blue-700',
  adulto: 'bg-green-100 text-green-700',
  tercera_edad: 'bg-amber-100 text-amber-700',
};

export const ClientFormTemplatesPanel = () => {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editSections, setEditSections] = useState([]);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/client-form-templates`, { withCredentials: true });
      setTemplates(res.data);
    } catch {
      toast.error('Error al cargar plantillas de cliente');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (tmpl) => {
    setEditingTemplate(tmpl);
    setEditSections(JSON.parse(JSON.stringify(tmpl.sections || [])));
  };

  const handleSave = async () => {
    if (!editingTemplate) return;
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/admin/client-form-templates/${editingTemplate.template_id}`,
        { sections: editSections },
        { withCredentials: true }
      );
      toast.success('Plantilla de cliente guardada');
      setEditingTemplate(null);
      fetchTemplates();
    } catch {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async (templateId) => {
    setResetting(templateId);
    try {
      await axios.post(`${API_URL}/api/admin/client-form-templates/${templateId}/reset`, {}, { withCredentials: true });
      toast.success('Plantilla restaurada a valores por defecto');
      fetchTemplates();
    } catch {
      toast.error('Error al restaurar');
    } finally {
      setResetting(null);
    }
  };

  const updateSection = (idx, section) => {
    const sections = [...editSections];
    sections[idx] = section;
    setEditSections(sections);
  };

  const removeSection = (idx) => {
    const sections = [...editSections];
    sections.splice(idx, 1);
    setEditSections(sections);
  };

  const addSection = () => {
    setEditSections([...editSections, {
      id: `section_${Date.now()}`,
      title: t('formEditor.newSection'),
      description: '',
      repeatable: false,
      max_repeat: 1,
      fields: [{ id: `field_${Date.now()}`, type: 'text', label: t('formEditor.newField'), placeholder: '', required: false }]
    }]);
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12 text-muted-foreground">{t('common.loading')}</div>;
  }

  return (
    <div className="space-y-6" data-testid="client-form-templates-panel">
      <Card className="card-elevated">
        <CardHeader>
          <div>
            <CardTitle className="font-heading">Formularios de Cliente</CardTitle>
            <CardDescription>
              Formularios que el cliente rellena con informacion sobre el protagonista del libro. Hay uno por cada perfil de destinatario.
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {templates.map(tmpl => {
            const sectionCount = tmpl.sections?.length || 0;
            const fieldCount = tmpl.sections?.reduce((sum, s) => sum + (s.fields?.length || 0), 0) || 0;

            return (
              <div
                key={tmpl.template_id}
                className="flex items-center justify-between p-4 rounded-lg border hover:bg-muted/20 transition-colors"
                data-testid={`client-tmpl-${tmpl.profile_type}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <ClipboardList className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{tmpl.name}</span>
                      <Badge className={`text-[10px] ${PROFILE_COLORS[tmpl.profile_type] || 'bg-muted'}`}>
                        {PROFILE_LABELS[tmpl.profile_type] || tmpl.profile_type}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {sectionCount} secciones, {fieldCount} campos
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8"
                    onClick={() => setPreviewTemplate(tmpl)}
                    title="Vista previa"
                    data-testid={`preview-client-tmpl-${tmpl.profile_type}`}
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8"
                    onClick={() => handleEdit(tmpl)}
                    title="Editar"
                    data-testid={`edit-client-tmpl-${tmpl.profile_type}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8"
                    onClick={() => handleReset(tmpl.template_id)}
                    disabled={resetting === tmpl.template_id}
                    title="Restaurar por defecto"
                    data-testid={`reset-client-tmpl-${tmpl.profile_type}`}
                  >
                    {resetting === tmpl.template_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            );
          })}
          {templates.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-6">No hay plantillas de cliente.</p>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog - Full section/field editor */}
      <Dialog open={!!editingTemplate} onOpenChange={(open) => !open && setEditingTemplate(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-primary" />
              Editar: {editingTemplate?.name}
              <Badge className={`text-[10px] ${PROFILE_COLORS[editingTemplate?.profile_type] || ''}`}>
                {PROFILE_LABELS[editingTemplate?.profile_type] || ''}
              </Badge>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Edita las secciones y campos del formulario de cliente. Los cambios se aplicaran a los nuevos libros de este perfil.
            </p>

            <div className="space-y-3">
              {editSections.map((section, idx) => (
                <SectionEditor
                  key={section.id}
                  section={section}
                  onChange={(s) => updateSection(idx, s)}
                  onDelete={() => removeSection(idx)}
                />
              ))}
            </div>

            <Button variant="outline" className="w-full gap-2" onClick={addSection}>
              <Plus className="w-4 h-4" /> {t('formEditor.addSection')}
            </Button>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setEditingTemplate(null)}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="save-client-tmpl-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {saving ? t('notifications.saving') : t('common.save')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewTemplate} onOpenChange={(open) => !open && setPreviewTemplate(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-primary" />
              {previewTemplate?.name}
              <Badge className={`text-[10px] ${PROFILE_COLORS[previewTemplate?.profile_type] || ''}`}>
                {PROFILE_LABELS[previewTemplate?.profile_type] || ''}
              </Badge>
            </DialogTitle>
          </DialogHeader>
          {previewTemplate && (
            <div className="space-y-4">
              {previewTemplate.sections?.map((section, si) => (
                <div key={section.id || si} className="border rounded-lg overflow-hidden">
                  <div className="px-4 py-3 bg-muted/30">
                    <h4 className="font-medium text-sm">{section.title}</h4>
                    {section.description && <p className="text-xs text-muted-foreground">{section.description}</p>}
                    <div className="flex gap-2 mt-1">
                      <Badge variant="outline" className="text-[10px]">{section.fields?.length || 0} campos</Badge>
                      {section.repeatable && <Badge variant="outline" className="text-[10px] bg-blue-50">Repetible x{section.max_repeat}</Badge>}
                    </div>
                  </div>
                  <div className="p-4 space-y-2">
                    {section.fields?.map((field, fi) => (
                      <div key={field.id || fi} className="flex items-start gap-2 text-xs">
                        <Badge variant="secondary" className="text-[9px] flex-shrink-0 mt-0.5">{field.type}</Badge>
                        <div>
                          <span className="font-medium">{field.label}</span>
                          {field.required && <span className="text-red-500 ml-0.5">*</span>}
                          {field.placeholder && (
                            <p className="text-muted-foreground italic">{field.placeholder}</p>
                          )}
                          {field.options && (
                            <p className="text-muted-foreground">Opciones: {field.options.map(o => o.label).join(', ')}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
